import { Component, h, Element, Build } from '@stencil/core';

type DomElement = globalThis.Element;

@Component({
    tag: 'web-desktop-menu',
    styleUrl: './desktop-menu.scss',
    shadow: true
})
export class DesktopMenu {
    @Element() hostElement: HTMLWebNavigationElement;

    private clickHandler = (e: Event) => this.onDocumentClick(e);

    private menuElements: Map<DomElement, DomElement> | undefined;
    private mobileNavigation: DomElement | undefined;

    connectedCallback() {
        document.addEventListener('click', this.clickHandler, false);
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.clickHandler);
    }

    componentDidLoad() {
        if (Build.isBrowser) {
            const defaultSlot = this.hostElement.shadowRoot.querySelector('slot');
            defaultSlot.addEventListener('slotchange', (event: Event) => this.onSlotChange(event));
            defaultSlot.dispatchEvent(new Event('slotchange')); //Trigger once because it has happened before DidLoad
        }
    }

    /**
     * Handles the slot change event.
     * @param event The event object.
     */
    private onSlotChange({ currentTarget }: Event): void {
        const slot: HTMLSlotElement = currentTarget as HTMLSlotElement;
        if (slot) {
            const items: Element[] = slot.assignedElements(); //<web-navigation-item>
            const [desktopElement, mobileElement] = items;
            const buttons: Array<Element> = Array.from(
                desktopElement.querySelectorAll('.nav-item.menu > [data-menu-button]')
            );
            this.menuElements = new Map(buttons.map((button: Element) => [button, button.parentNode as Element]));

            this.setSelectedMenuItems(desktopElement);

            this.mobileNavigation = mobileElement;
        }
    }

    /**
     * Sets the selected menu items based on the current URL.
     * @param element The element to search for menu items.
     */
    private setSelectedMenuItems(element: Element): void {
        Array.from(element.querySelectorAll('nav .nav-item')).forEach((menuItem) => {
            const { pathname, hostname, hash } = window.location;

            const matchesHost = (a: HTMLAnchorElement) => a.hostname === hostname;
            const matchesPath = (a: HTMLAnchorElement) => a.pathname === pathname || `${a.pathname}/` === pathname;
            const matchesExact = (a: HTMLAnchorElement) => matchesPath(a) && a.hash === hash;
            const matchesPartial = (a: HTMLAnchorElement) => a.pathname !== '/' && pathname.includes(a.pathname);

            const links = Array.from(menuItem.querySelectorAll('a')).filter(matchesHost);
            const link = links.find(matchesExact) || links.find(matchesPath) || links.find(matchesPartial);

            links.forEach((a) => a.classList.toggle('selected', a === link));
            menuItem.classList.toggle('selected', !!link);
        });
    }

    /**
     * Handles document click events.
     * @param event The event object.
     */
    private onDocumentClick(event: Event) {
        const target = event.target as Element;
        if (!this.hostElement.contains(target) || this.menuElements?.get(target)?.classList.contains('active')) {
            if (!target.closest('.sub-menu')) {
                this.closeMenus();
            }
            this.closeMobileMenu();
        } else {
            this.onMenuClick(target);
            this.onMobileMenuToggle(target, event);
        }
    }

    /**
     * Closes all menus.
     */
    private closeMenus() {
        this.menuElements?.forEach((element) => element.classList.remove('active'));
    }

    /**
     * Handles menu click events.
     * @param element The element that was clicked.
     */
    private onMenuClick(element: Element | undefined) {
        if (!element.closest('.sub-menu')) {
            this.closeMenus();
        }
        if (element) {
            this.menuElements?.get(element)?.classList.toggle('active');
        }
    }

    /**
     * Closes the mobile menu.
     */
    private closeMobileMenu() {
        this.mobileNavigation?.classList.remove('open');
    }

    /**
     * Handles mobile menu toggle events.
     * @param element The element that was clicked.
     * @param event The event object.
     */
    private onMobileMenuToggle(element: Element | undefined, event: Event) {
        if (element && element.matches('[data-button-mobile-toggle]')) {
            event.preventDefault();
            event.stopPropagation();

            this.mobileNavigation?.classList.toggle('open');
        }
    }

    public render = () => <slot />;
}
